import dayjs from 'utils/dayjs';
import {useReducer} from 'react';
import {merge} from 'lodash-es';
import {v4 as uuidV4} from 'uuid';

import {monthDayYear} from 'utils/patterns';
import {
    DAILY,
    EXCEPTION,
    CONTINUOUS,
} from 'pages/inventory/utils/inventory-rule-types';
import {
    displayDateFormat,
    display24HourFormat,
    displayTimeMeridiem,
} from 'utils/date-time';

// Todo: Consider a form library and validation library to handle all of this heavy lifting

const monthDayYearRegExp = new RegExp(monthDayYear);

// Repeat end types
export const SPECIFIC_DATE = 'specific-date';
export const NEVER_END = 'never-end';

export const START_DATE_SELECTED = 'START_DATE_SELECTED';
export const END_DATE_SELECTED = 'END_DATE_SELECTED';
export const START_TIME_SELECTED = 'START_TIME_SELECTED';
export const END_TIME_SELECTED = 'END_TIME_SELECTED';
export const ALL_DAY_SELECTED = 'ALL_DAY_SELECTED';
export const REPEATING_TOGGLED = 'REPEATING_TOGGLED';
export const FORM_RESET = 'FORM_RESET';
export const REPEAT_END_DATE_SELECTED = 'REPEAT_END_DATE_SELECTED';
export const REPEAT_END_TYPE_SELECTED = 'REPEAT_END_TYPE_SELECTED';
export const HELP_GUIDE_LOADED = 'HELP_GUIDE_LOADED';
export const HELP_GUIDE_ERROR = 'HELP_GUIDE_ERROR';
export const HELP_GUIDE_CLICKED = 'HELP_GUIDE_CLICKED';

export const ERRORS = {
    INVALID_END_DATE: 'INVALID_END_DATE',
    INVALID_DATE_FORMAT: 'INVALID_DATE_FORMAT',
};

/**
 * Gets the initial state
 *
 * @returns {TExceptionModalState} - Initial state properties
 */
export const getInitialState = () => {
    const now = dayjs().startOf('day');
    const today = now.format('dddd').toLowerCase();

    return {
        endTime: dayjs().endOf('day'),
        endDate: now,
        dateError: '',
        startDate: now,
        startTime: dayjs().startOf('day'),
        isAllDay: true,
        isEditing: false,
        timeError: false,
        isRepeating: false,
        canRepeat: true,
        repeatToggleKey: uuidV4(),
        duration: null,
        scheduleType: EXCEPTION,
        formKey: uuidV4(),
        helpGuideHref: '',
        repeatEndDateError: false,
        repeatEndDate: now,
        defaultRepeatEndsType: null,
        defaultRepeatingDays: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
            [today]: true,
        },
    };
};

/**
 * Api comes back with an invalid format for time, convert it to a valid format
 *
 * @param {String} time - Date time in utc format
 * @returns {dayjs.Dayjs} Moment object
 */
const timeToFakeMoment = (time) => {
    return dayjs(time, display24HourFormat);
};

/**
 * Calculates the difference between start datetime and end datetime, in hours
 *
 * @param {dayjs.Dayjs} startDate - Date the exception starts as a dayjs object
 * @param {dayjs.Dayjs} endDate - Date the exception ends as a dayjs object
 * @param {dayjs.Dayjs} startTime - Time the exception starts as a dayjs object
 * @param {dayjs.Dayjs} endTime - Time the exception ends as a dayjs object
 * @returns {Number} - Number representing the difference between start datetime
 *   and end datetime in hours
 */
export const getDuration = ({startDate, endDate, startTime, endTime}) => {
    const getDateTime = (date, time) => {
        return dayjs(
            `${date.format(displayDateFormat)}${
                time ? ` ${time?.format(displayTimeMeridiem)}` : ''
            }`
        );
    };

    const startDateTime = getDateTime(startDate, startTime);
    const endDateTime = getDateTime(endDate, endTime);

    return endDateTime.diff(startDateTime, 'hour');
};

/**
 * Determines if this exception is repeatable. If start or end time controls
 * visible and have no value selected => false Otherwise, if duration is not
 * negative and less or equal to 7 days => true
 *
 * @returns {Boolean} - True = exception is repeatable, false = it is not
 */
const canRepeat = ({duration, startTime, endTime, isAllDay}) => {
    if (!isAllDay && (!startTime || !endTime)) {
        return false;
    }

    return Boolean(duration >= 0 && duration <= 168);
};

/**
 * Determines the inventory rule type If duration > 0 and <= 24 => daily If
 * duration > 24 and <= 168 (7 days) => continuous Otherwise it's an exception type
 *
 * @param {Number} duration Length of exception
 * @param {Boolean} isRepeating Whether repeat control is on or not
 * @returns {String} Inventory rule schedule type
 */
export const getScheduleType = (duration, isRepeating) => {
    if (!isRepeating || duration > 168) {
        return EXCEPTION;
    }

    if (duration <= 24) {
        return DAILY;
    }

    return CONTINUOUS;
};

/**
 * Synchronizes the date value
 *
 * Used to compare two dates when syncrhronizing date inputs. If the prevDate is
 * before the newDate then the newDate is returned, otherwise the prevDate is returned
 *
 * @param {String | dayjs.ment} prevDate - The date being compared
 * @param {String | dayjs.Dayjs} newDate - The new date used to check if the
 *   prevDate is before
 * @returns {dayjs.Dayjs} - Updated date value in dayjs format
 */
const syncDates = (prevDate, newDate) =>
    dayjs(prevDate).isBefore(dayjs(newDate, displayDateFormat))
        ? dayjs(newDate, displayDateFormat)
        : dayjs(prevDate);

/**
 * Updates the datetime state
 *
 * @param {object} state - Initial state
 * @param {date} inputDate - Date value of the input
 * @param {object} newState - New state to be merged
 * @returns {TDateTimeState} - Returns updated state
 */
const updateDateTime = (state, {inputDate, ...newState}) => {
    const combinedState = merge(
        state,
        {
            defaultRepeatingDays: {
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false,
                sunday: false,
            },
        },
        newState
    );

    let dateError = '';

    // if the date is poorly formatted
    if (inputDate && !monthDayYearRegExp.test(inputDate)) {
        dateError = ERRORS.INVALID_DATE_FORMAT;
    }

    // if the end date is older than the start date
    if (
        !combinedState.startDate.isSame(combinedState.endDate) &&
        combinedState.startDate.isAfter(combinedState.endDate)
    ) {
        dateError = ERRORS.INVALID_END_DATE;
    }

    const duration = getDuration({
        startDate: combinedState.startDate,
        endDate: combinedState.endDate,
        startTime: combinedState.startTime,
        endTime: combinedState.endTime,
    });

    const newCanRepeat = canRepeat({
        duration,
        startTime: combinedState.startTime,
        endTime: combinedState.endTime,
        isAllDay: combinedState.isAllDay,
    });

    const newIsRepeating = newCanRepeat && combinedState.isRepeating;

    // if we're adding a repeating exception, set the default repeating day to the start date
    if (!combinedState.isEditing) {
        combinedState.defaultRepeatingDays[
            combinedState.startDate.format('dddd').toLowerCase()
        ] = true;
    }

    return {
        ...combinedState,
        dateError,
        timeError:
            combinedState.startDate.isSame(combinedState.endDate) &&
            combinedState.startTime?.isAfter(combinedState.endTime),
        repeatToggleKey: uuidV4(),
        duration,
        canRepeat: newCanRepeat,
        isRepeating: newIsRepeating,
        scheduleType: getScheduleType(duration, newIsRepeating),
        repeatEndDateError:
            newIsRepeating &&
            combinedState.repeatEndDate?.isBefore(combinedState.endDate),
    };
};

const normalizeException = ({
    validTo,
    endTime,
    validFrom,
    startTime,
    scheduleType,
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
    sunday,
    continuousEndDow,
    continuousStartDow,
    ...rest
}) => {
    const startDate = dayjs(validFrom);
    let endDate = dayjs(validTo);

    if (scheduleType === DAILY) {
        endDate = startDate;
    }

    if (scheduleType === CONTINUOUS) {
        /*
         * Our BE considers Monday as first day of week, but Moment think it's Sunday
         * This function does the conversion
         */
        const dayjsEndDow = (continuousEndDow + 8) % 7;

        endDate =
            continuousStartDow < continuousEndDow
                ? startDate.day(dayjsEndDow)
                : startDate.day(7 + dayjsEndDow);
    }

    const formattedStartTime =
        scheduleType === EXCEPTION
            ? dayjs(validFrom)
            : timeToFakeMoment(startTime);

    let formattedEndTime = endTime ? timeToFakeMoment(endTime) : null;

    if (scheduleType === EXCEPTION) {
        formattedEndTime = dayjs(validTo);
    }

    const isAllDay =
        scheduleType === EXCEPTION
            ? // it is all day if the validTo time is 11:59pm
              dayjs(validTo).diff(dayjs().endOf('day'), 'minute') === 0
            : dayjs(formattedEndTime).diff(dayjs().endOf('day'), 'minute') ===
              0;

    return updateDateTime(
        {},
        {
            ...rest,
            isEditing: true,
            scheduleType,
            startDate,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            isAllDay,
            endDate,
            isRepeating: scheduleType === DAILY || scheduleType === CONTINUOUS,
            defaultRepeatingDays: {
                monday,
                tuesday,
                wednesday,
                thursday,
                friday,
                saturday,
                sunday,
            },
            defaultRepeatEndsType: validTo ? SPECIFIC_DATE : NEVER_END,
            repeatEndDate: validTo ? dayjs(validTo) : endDate,
        }
    );
};

export const reducer = (state, {type, payload}) => {
    switch (type) {
        case START_DATE_SELECTED: {
            return updateDateTime(state, {
                inputDate: payload,
                startDate: dayjs(payload, displayDateFormat),
                endDate: syncDates(state.endDate, payload),
                repeatEndDate: syncDates(state.repeatEndDate, payload),
                endDateInputKey: uuidV4(),
                repeatEndDateInputKey: uuidV4(),
            });
        }

        case END_DATE_SELECTED:
            return updateDateTime(state, {
                inputDate: payload,
                endDate: dayjs(payload, displayDateFormat),
                repeatEndDate: syncDates(state.repeatEndDate, payload),
                repeatEndDateInputKey: uuidV4(),
            });

        case START_TIME_SELECTED:
            return updateDateTime(state, {
                startTime: dayjs(payload, displayTimeMeridiem),
            });

        case END_TIME_SELECTED:
            return updateDateTime(state, {
                endTime: dayjs(payload, displayTimeMeridiem),
            });

        case ALL_DAY_SELECTED:
            return updateDateTime(state, {
                isAllDay: payload,
                startTime: payload ? dayjs().startOf('day') : null,
                endTime: payload ? dayjs().endOf('day') : null,
            });

        case REPEATING_TOGGLED: {
            const duration = getDuration({
                startDate: state.startDate,
                endDate: state.endDate,
                startTime: state.startTime,
                endTime: state.endTime,
            });

            return {
                ...state,
                isRepeating: payload,
                scheduleType: getScheduleType(duration, payload),
                repeatEndDate: state.repeatEndDate,
                repeatEndDateError: false,
            };
        }

        case FORM_RESET:
            return {
                ...getInitialState(),
                helpGuideHref: state.helpGuideHref,
            };

        case REPEAT_END_DATE_SELECTED: {
            const newRepeatEndDate = dayjs(payload, displayDateFormat);

            return {
                ...state,
                repeatEndDate: newRepeatEndDate,
                repeatEndDateError: newRepeatEndDate.isBefore(state.endDate),
            };
        }

        case REPEAT_END_TYPE_SELECTED:
            return {
                ...state,
                repeatEndType: payload,
                repeatEndDateError:
                    payload === SPECIFIC_DATE &&
                    state.repeatEndDate?.isBefore(state.endDate),
            };

        case HELP_GUIDE_LOADED:
            return {
                ...state,
                helpGuideHref: payload,
            };

        // `helpGuideError` is a flag to identify if the Help Guide URL was successfully retrieved from Prismic
        case HELP_GUIDE_ERROR:
            return {
                ...state,
                helpGuideError: true,
            };

        /*
         * The Help Guide error is not shown to user until they click the Help Guide link
         * `showHelpGuideError` determines if the error should be shown
         */
        case HELP_GUIDE_CLICKED:
            return {
                ...state,
                showHelpGuideError: true,
            };

        default:
            return state;
    }
};

const ExceptionModalState = (exception) => {
    const [state, dispatch] = useReducer(
        reducer,
        Object.keys(exception).length
            ? normalizeException(exception)
            : getInitialState()
    );

    const createAction = (type) => (payload) => dispatch({type, payload});

    return [
        state,
        {
            selectEndTime: createAction(END_TIME_SELECTED),
            selectStartTime: createAction(START_TIME_SELECTED),
            selectEndDate: createAction(END_DATE_SELECTED),
            selectStartDate: createAction(START_DATE_SELECTED),
            toggleAllDay: createAction(ALL_DAY_SELECTED),
            toggleRepeating: createAction(REPEATING_TOGGLED),
            resetForm: createAction(FORM_RESET),
            selectRepeatEndDate: createAction(REPEAT_END_DATE_SELECTED),
            selectRepeatEndType: createAction(REPEAT_END_TYPE_SELECTED),
            setHelpGuideHref: createAction(HELP_GUIDE_LOADED),
            setHelpGuideError: createAction(HELP_GUIDE_ERROR),
            displayHelpGuideError: createAction(HELP_GUIDE_CLICKED),
        },
    ];
};

export default ExceptionModalState;
